import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CohortSplit } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Loading, ErrorNote, useTooltip, ViewTitle } from '../ui'

const SPLITS: Array<{ value: CohortSplit; label: string }> = [
  { value: 'channel', label: 'Acquisition channel' },
  { value: 'practice', label: 'Optometrist practice' },
  { value: 'month', label: 'Month paid' },
  { value: 'rx', label: 'Reading prescription' },
  { value: 'age', label: 'Age range' },
  { value: 'saved', label: 'Your cohorts' },
]

const heatIndex = (v: number) =>
  v >= 70 ? 6 : v >= 55 ? 5 : v >= 42 ? 4 : v >= 30 ? 3 : v >= 18 ? 2 : v >= 6 ? 1 : 0

export function Cohorts() {
  const [split, setSplit] = useState<CohortSplit>('channel')
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.cohorts.queryOptions({ split }))
  const { node: tipNode, bind } = useTooltip()

  if (q.error) return <ErrorNote error={q.error} />

  const data = q.data
  const headline = data && split === 'channel' ? readChannelGap(data.rows) : null

  return (
    <>
      <ViewTitle suit="heart">Who actually trains</ViewTitle>
      <p className="view-sub">
        Cross-correlate any cohort against the training milestones that matter. This is the screen
        that tells you whether a channel, a price point or a prescription band produces patients who
        keep going.
      </p>

      <Panel>
        <div className="controls">
          <label className="ctl">
            Split by{' '}
            <select value={split} onChange={(e) => setSplit(e.target.value as CohortSplit)}>
              {SPLITS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {data && (
            <span className="chip num">
              <span className="dot ok" />
              {data.paidInScope.toLocaleString()} paid buyers in scope
            </span>
          )}
        </div>

        {!data ? (
          <Loading what="cohorts" />
        ) : (
          <div className="scroll-x">
            <table className="mx">
              <thead>
                <tr>
                  <th className="rowh">Cohort</th>
                  {(data.rows[0]?.milestones ?? []).map((m) => (
                    <th key={m.sessions}>{m.sessions === 1 ? 'Started' : `${m.sessions}+`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.key}>
                    <td className="rowh">
                      {row.key}
                      <span className="n num">n={row.n}</span>
                    </td>
                    {row.milestones.map((m) => {
                      const i = heatIndex(m.pct)
                      return (
                        <td
                          key={m.sessions}
                          className="cell"
                          style={{ background: `var(--heat-${i})`, color: `var(--heat-${i}-ink)` }}
                          {...bind(
                            <>
                              <b>
                                {row.key} · {m.sessions === 1 ? 'started' : `${m.sessions}+ sessions`}
                              </b>
                              <div className="r">
                                <span>
                                  {m.count} of {row.n} paid
                                </span>
                                <span>{m.pct}%</span>
                              </div>
                            </>,
                          )}
                        >
                          {m.pct}%
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {headline ? (
          <p className="footnote">
            <strong>Reading this:</strong> {headline}
          </p>
        ) : (
          <p className="footnote">
            Cells are the share of that cohort’s <em>paid</em> buyers who reached the milestone.
            {split === 'saved'
              ? ' Each cohort made on the Buckets screen is shown whatever its size, against everybody who paid. A cohort bought last week has had no time to train: read it against its age.'
              : ' Cohorts under 12 patients are hidden — too small to read anything into.'}
          </p>
        )}
      </Panel>
      {tipNode}
    </>
  )
}

function readChannelGap(rows: Array<{ key: string; milestones: Array<{ sessions: number; pct: number }> }>) {
  const at20 = (key: string) =>
    rows.find((r) => r.key === key)?.milestones.find((m) => m.sessions === 20)?.pct
  const opto = at20('Optometrist')
  const direct = at20('Direct web')
  if (opto == null || direct == null) return null
  const gap = opto - direct
  return `${opto}% of optometrist-referred buyers reach 20 sessions versus ${direct}% of direct-web buyers — a ${gap} point gap. If that holds as the data grows it is an argument for shifting spend toward the practice channel, and for finding out what the optometrist says at handover that the website doesn’t.`
}
